// HTM — the admin surface for the HTM (Pipes.ai) aged-lead resale stage
// (apps/rtb-api/src/htm_dispatch.ts): a rate(5 minutes) tick that, 72 h after
// each over-$10K form-fill, PINGS HTM and — only on `success:true` — POSTS the
// lead with DB-held fields only, recording every decision in its OWN
// `htm-sales` ledger. Gated: sec-lead-sales (same section as Lead Sales).
//
// Reference: stages/02_prp/output/htm_aged_leads_prp.md (Task 7, AC10, AC12,
// F6, D13). Cloned from pages/LeadSalesWitzzy.tsx — the SHAPE only (master
// toggle w/ confirm-to-enable, window presets + 15 s poll stats strip, config
// Card + Save, write-only paste field). NOT the send-window editor.
//
// Two data planes, deliberately separate (same split as LeadSalesWitzzy.tsx):
//   - LIVE (stats): windowed, auto-refreshed every 15 s, pauses while the tab
//     is hidden. Ledger outcomes split LIVE vs TEST by each row's own
//     test_mode + HTM's rejection reasons, and the considered / skipped /
//     pinged funnel off the htm-dispatch Logs Insights group.
//   - CONFIG (the htm_config singleton): loaded once, mutated by the two
//     flag toggles (PUT …/flags) and the config form (PUT …/config).
//
// 🔒 Live-safety — the two confirm gates (PRP AC12, F6):
//   1. ENABLE requires a deliberate confirm step (Off is immediate). The
//      confirm copy says TEST or LIVE, so the operator always knows which.
//   2. Turning test_mode OFF while the stage is ENABLED requires a confirm
//      step (it turns test sends into real sales). Turning test_mode ON is
//      always immediate — it is the safe direction.
//   A prominent "TEST MODE" badge sits on the stats strip whenever test_mode
//   is on, so test counts are never read as revenue.
//   `enabled` / `test_mode` are NEVER sent by a config Save — only PUT …/flags
//   flips them (the server ignores them on the config PUT anyway).
//   The api key is WRITE-ONLY: pasted here, sent as `api_key_new` ONLY when
//   non-blank, written to Secrets Manager by admin-api; this page only ever
//   sees the masked hint, never the key and never the ARN (PRP D13 / F7).
//   The age field carries a client guard mirroring the server's 400
//   `invalid_age` — HTM never receives a lead younger than 72 h.

import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";

import {
  api,
  ApiError,
  HTM_CATCHUP_MAX_MS,
  HTM_CATCHUP_MIN_MS,
  HTM_MIN_AGE_MS,
  HTM_SENDS_PER_TICK_MAX,
  HTM_TIERS,
  type HtmConfig,
  type HtmConfigResponse,
  type HtmConfigUpdate,
  type HtmFlagsUpdate,
  type HtmLedgerStatus,
  type HtmOutcomeCounts,
  type HtmPingFields,
  type HtmStats,
  type HtmTier,
} from "../lib/api";
import {
  buttonStyle,
  Card,
  EmptyState,
  FieldLabel,
  inputStyle,
  PageHeader,
  Pill,
  Skeleton,
  StatCard,
  tokens,
} from "../lib/theme";

const POLL_MS = 15_000;
const HOUR_MS = 60 * 60 * 1000;
const MIN_MS = 60 * 1000;

// ---------------------------------------------------------------------------
// Window presets (PRP AC12): Today (PT) / Yesterday (PT) / Last 24 h / Last 7 d.
// "Today (PT)" sends NO window param, so the server applies its Pacific-
// midnight default (the LeadSalesWitzzy convention). "Yesterday (PT)" is
// computed HERE, client-side, as explicit start/end epoch-seconds — and it is
// recomputed on every fetch, so a page left open past midnight moves with it.
// ---------------------------------------------------------------------------

type PresetId = "today" | "yesterday" | "24h" | "7d";

const PRESETS: Array<{ id: PresetId; label: string }> = [
  { id: "today", label: "Today (PT)" },
  { id: "yesterday", label: "Yesterday (PT)" },
  { id: "24h", label: "Last 24 h" },
  { id: "7d", label: "Last 7 d" },
];

type StatsOpts = { windowSec?: number; start?: number; end?: number };

// Pacific wall-clock formatter — the same zone + convention the server uses
// for its default "Today" window (routes/htm.ts:pacificDayStartMs).
const PT_WALL_FMT = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Los_Angeles",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

/** Pacific wall clock at `ms`, as {y, mo, d} + the UTC offset (wall − real,
 *  in ms: −7 h PDT, −8 h PST). */
function pacificWall(ms: number): { y: number; mo: number; d: number; offsetMs: number } {
  const parts = PT_WALL_FMT.formatToParts(new Date(ms));
  const val = (t: string): number => Number(parts.find((p) => p.type === t)?.value ?? "0");
  const y = val("year");
  const mo = val("month");
  const d = val("day");
  const h = val("hour") % 24; // hour12:false can emit "24" at midnight
  const wallAsUtc = Date.UTC(y, mo - 1, d, h, val("minute"), val("second"));
  return { y, mo, d, offsetMs: wallAsUtc - Math.floor(ms / 1000) * 1000 };
}

/** Epoch-ms of the start of the Pacific day containing `nowMs`. Adapted from
 *  routes/htm.ts:pacificDayStartMs (itself lead_sales.ts's) with ONE fix: the
 *  offset is re-read AT the candidate midnight, not at `nowMs`. On a DST-change
 *  day the two differ by an hour (the server copy would start Sun Nov 1 2026 at
 *  01:00 PDT and Sun Mar 8 2026 at 23:00 PST the day before); re-reading at the
 *  candidate lands on the true midnight, since US DST never changes at 00:00. */
export function pacificDayStartMs(nowMs: number): number {
  const { y, mo, d, offsetMs } = pacificWall(nowMs);
  const midnightWallAsUtc = Date.UTC(y, mo - 1, d, 0, 0, 0);
  const guess = midnightWallAsUtc - offsetMs;
  return midnightWallAsUtc - pacificWall(guess).offsetMs;
}

/** Resolve a preset into api.htmStats opts at `nowMs`. Yesterday (PT) =
 *  [yesterday's Pacific midnight, today's Pacific midnight] in epoch-SECONDS
 *  (the server's `start`/`end` contract). */
export function resolveStatsOpts(preset: PresetId, nowMs: number): StatsOpts | undefined {
  switch (preset) {
    case "today":
      return undefined; // server default = Pacific Today
    case "yesterday": {
      const todayStartMs = pacificDayStartMs(nowMs);
      const yesterdayStartMs = pacificDayStartMs(todayStartMs - 1);
      return {
        start: Math.floor(yesterdayStartMs / 1000),
        end: Math.floor(todayStartMs / 1000),
      };
    }
    case "24h":
      return { windowSec: 24 * 3600 };
    case "7d":
      return { windowSec: 7 * 24 * 3600 };
  }
}

function fmtCount(n: number | undefined): string {
  return (n ?? 0).toLocaleString();
}

// ---------------------------------------------------------------------------
// Tiers — the four HTM tiers only. There is NO below_10k option (PRP D8): it is
// not in HtmTier, the reader drops it and the server PUT 400s it.
// ---------------------------------------------------------------------------

const TIER_LABELS: Record<HtmTier, string> = {
  over_10k: "$10k–$20k",
  over_20k: "$20k–$30k",
  over_30k: "$30k–$40k",
  over_40k: "$40k+",
};

// The ledger outcome tiles, in funnel order. Rendered twice — LIVE and TEST.
const OUTCOME_TILES: Array<{ status: HtmLedgerStatus; label: string; hint: string }> = [
  { status: "posted", label: "Posted", hint: "ping accepted, post accepted" },
  { status: "post_rejected", label: "Post rejected", hint: "HTM refused the post — reason below" },
  { status: "ping_rejected", label: "Ping rejected", hint: "HTM declined the ping — reason below" },
  { status: "blocked_exclusive", label: "Blocked (exclusive)", hint: "sold to an exclusive buyer since the form-fill" },
  { status: "blocked_witzzy", label: "Blocked (Witzzy)", hint: "called the Witzzy number since the form-fill" },
  { status: "missing_fields", label: "Missing fields", hint: "no first name, valid email or zip" },
  { status: "failed", label: "Failed", hint: "transport failure — retried next tick" },
  { status: "in_flight", label: "In flight", hint: "claimed, ping/post in progress" },
];

// ---------------------------------------------------------------------------
// Config form state. Numeric fields are kept as strings so the inputs can be
// cleared mid-edit; they're parsed + validated on Save. `enabled` / `test_mode`
// are NOT here — the two flag toggles own them.
// ---------------------------------------------------------------------------

interface FormState {
  ageHours: string;
  catchupMin: string;
  sendsPerTick: string;
  tiers: HtmTier[];
  pingFields: HtmPingFields;
  pingUrl: string;
  postUrl: string;
  /** Write-only paste field — always starts empty; a new key is opt-in per Save. */
  apiKeyNew: string;
}

function configToForm(c: HtmConfig): FormState {
  return {
    ageHours: String(c.age_ms / HOUR_MS),
    catchupMin: String(c.catchup_ms / MIN_MS),
    sendsPerTick: String(c.sends_per_tick),
    tiers: HTM_TIERS.filter((t) => c.tiers.includes(t)),
    pingFields: c.ping_fields,
    pingUrl: c.ping_url,
    postUrl: c.post_url,
    apiKeyNew: "",
  };
}

/** The endpoint-URL rule the server applies (routes/htm.ts:isHttpsUrl). */
function isHttpsUrl(v: string): boolean {
  return v.startsWith("https://") && v.length > "https://".length;
}

interface FieldErrors {
  age: string | null;
  catchup: string | null;
  sends: string | null;
  tiers: string | null;
  urls: string | null;
}

/** Every client guard, field by field — the single source both the live
 *  per-field alerts and buildUpdate read, so the two can never disagree. Each
 *  mirrors a server 400 (invalid_age / invalid_catchup / invalid_body /
 *  invalid_tiers / invalid_url). */
function fieldErrors(form: FormState, minAgeMs: number, tickCap: number): FieldErrors {
  const minAgeHours = minAgeMs / HOUR_MS;

  let age: string | null = null;
  const ageH = Number(form.ageHours);
  if (form.ageHours.trim() === "" || !Number.isFinite(ageH)) {
    age = "Age must be a number of hours.";
  } else if (Math.round(ageH * HOUR_MS) < minAgeMs) {
    // 🔴 The 72 h floor — mirrors the server's 400 invalid_age (PRP AC2).
    age = `Age must be at least ${minAgeHours} hours — HTM never receives a lead younger than that.`;
  }

  let catchup: string | null = null;
  const cMin = Number(form.catchupMin);
  const cMs = Math.round(cMin * MIN_MS);
  if (
    form.catchupMin.trim() === "" ||
    !Number.isFinite(cMin) ||
    cMs < HTM_CATCHUP_MIN_MS ||
    cMs > HTM_CATCHUP_MAX_MS
  ) {
    catchup = `Catch-up must be ${HTM_CATCHUP_MIN_MS / MIN_MS}–${HTM_CATCHUP_MAX_MS / MIN_MS} minutes.`;
  }

  let sends: string | null = null;
  const spt = Number(form.sendsPerTick);
  if (form.sendsPerTick.trim() === "" || !Number.isInteger(spt) || spt < 1 || spt > tickCap) {
    sends = `Sends per tick must be a whole number from 1 to ${tickCap}.`;
  }

  const tiers = form.tiers.length === 0 ? "Pick at least one tier." : null;

  const urls =
    !isHttpsUrl(form.pingUrl.trim()) || !isHttpsUrl(form.postUrl.trim())
      ? "Ping URL and post URL must both start with https://."
      : null;

  return { age, catchup, sends, tiers, urls };
}

function hasError(e: FieldErrors): boolean {
  return e.age !== null || e.catchup !== null || e.sends !== null || e.tiers !== null || e.urls !== null;
}

/** Parse + validate the form into the PUT body, or return a message. An
 *  ALLOWLIST of the operator-editable knobs — never enabled / test_mode /
 *  api_key_secret_arn / api_key_hint — plus `api_key_new` ONLY when the paste
 *  field is non-blank after trimming (omitted entirely otherwise). */
function buildUpdate(
  form: FormState,
  minAgeMs: number,
  tickCap: number,
): { body: HtmConfigUpdate } | { error: string } {
  const e = fieldErrors(form, minAgeMs, tickCap);
  const first = e.age ?? e.catchup ?? e.sends ?? e.tiers ?? e.urls;
  if (first !== null) return { error: first };

  const body: HtmConfigUpdate = {
    age_ms: Math.round(Number(form.ageHours) * HOUR_MS),
    catchup_ms: Math.round(Number(form.catchupMin) * MIN_MS),
    sends_per_tick: Number(form.sendsPerTick),
    tiers: HTM_TIERS.filter((t) => form.tiers.includes(t)),
    ping_fields: form.pingFields,
    ping_url: form.pingUrl.trim(),
    post_url: form.postUrl.trim(),
  };
  const pasted = form.apiKeyNew.trim();
  if (pasted !== "") body.api_key_new = pasted;
  return { body };
}

/** Map a config-save failure to a friendly message (the server's 400 codes,
 *  routes/htm.ts:putConfigErrorCode). Falls back to the raw message. */
function friendlyConfigError(err: unknown): string {
  if (err instanceof ApiError) {
    const code =
      err.body && typeof err.body === "object" && "error" in err.body
        ? (err.body as { error?: unknown }).error
        : undefined;
    if (code === "invalid_age") {
      return "The server rejected the age — it must be at least 72 hours.";
    }
    if (code === "invalid_catchup") {
      return "The server rejected the catch-up window — it must be 5–360 minutes.";
    }
    if (code === "invalid_tiers") {
      return "The server rejected the tiers — pick at least one of the four over-$10K tiers.";
    }
    if (code === "invalid_url") {
      return "The server rejected an endpoint URL — both must start with https://.";
    }
    if (code === "invalid_body") {
      return "Some config fields are invalid as entered. If this page has been open a while, reload and try again.";
    }
    return err.message;
  }
  return err instanceof Error ? err.message : "Save failed";
}

export default function LeadSalesHtm() {
  // Config plane (the htm_config singleton + tick cap + the 72 h floor).
  const [config, setConfig] = useState<HtmConfigResponse | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  // The two flag toggles (PUT …/flags) and their confirm gates. Only one
  // confirm is ever open at a time.
  const [toggling, setToggling] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);
  const [confirmingEnable, setConfirmingEnable] = useState(false);
  const [confirmingTestOff, setConfirmingTestOff] = useState(false);

  // Live plane (stats), windowed + polled.
  const [preset, setPreset] = useState<PresetId>("today");
  const [stats, setStats] = useState<HtmStats | null>(null);
  const [liveError, setLiveError] = useState<string | null>(null);
  const [liveLoading, setLiveLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
  const [auto, setAuto] = useState(true);

  // --- Config load ---------------------------------------------------------
  const loadConfig = useCallback(() => {
    setConfigLoading(true);
    api
      .htmConfig()
      .then((r) => {
        if (!r || typeof r.config !== "object" || r.config === null) {
          throw new Error("Unexpected HTM config response — try reloading.");
        }
        setConfig(r);
        setForm(configToForm(r.config));
        setConfigError(null);
        setDirty(false);
      })
      .catch((e: unknown) =>
        setConfigError(e instanceof Error ? e.message : "Failed to load config"),
      )
      .finally(() => setConfigLoading(false));
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // --- Live load (stats) ----------------------------------------------------
  // The window is resolved at FETCH time (Date.now()), so Yesterday (PT) is
  // always yesterday relative to now, even on a page left open overnight.
  const load = useCallback(
    (showSpinner: boolean) => {
      if (showSpinner) setLiveLoading(true);
      api
        .htmStats(resolveStatsOpts(preset, Date.now()))
        .then((s) => {
          setStats(s);
          setLiveError(null);
          setRefreshedAt(new Date());
        })
        .catch((err: unknown) => {
          setLiveError(err instanceof Error ? err.message : "Failed to load");
        })
        .finally(() => setLiveLoading(false));
    },
    [preset],
  );

  // A ref so the poll timer always calls the latest fetch (current window)
  // without re-arming the interval on every preset change.
  const fetchRef = useRef<() => void>(() => {});
  useEffect(() => {
    fetchRef.current = () => load(false);
  }, [load]);

  // Re-fetch (with spinner) whenever the window changes.
  useEffect(() => {
    load(true);
  }, [load]);

  // Auto-refresh polling — pauses when the tab is hidden or auto is off.
  useEffect(() => {
    if (!auto) return;
    const id = setInterval(() => {
      if (document.visibilityState === "visible") fetchRef.current();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [auto]);

  // --- Flags (PUT …/flags) ---------------------------------------------------
  // Called ONLY from the immediate directions (Off, test_mode ON, test_mode
  // OFF while disabled) or from a confirm button.
  const setFlags = useCallback((flags: HtmFlagsUpdate) => {
    setToggling(true);
    setToggleError(null);
    api
      .setHtmFlags(flags)
      .then((cfg) => {
        setConfig((prev) =>
          prev
            ? { ...prev, config: cfg }
            : {
                config: cfg,
                tick_cap: HTM_SENDS_PER_TICK_MAX,
                min_age_ms: HTM_MIN_AGE_MS,
                age_defaulted: false,
              },
        );
        setConfirmingEnable(false);
        setConfirmingTestOff(false);
        // Refresh the strip so the TEST MODE badge tracks the flip now, not
        // on the next 15 s poll.
        fetchRef.current();
      })
      .catch((e: unknown) =>
        setToggleError(e instanceof Error ? e.message : "Failed to update"),
      )
      .finally(() => setToggling(false));
  }, []);

  // --- Config form -----------------------------------------------------------
  const update = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => (f ? { ...f, [key]: value } : f));
    setDirty(true);
  }, []);

  const toggleTier = useCallback((tier: HtmTier) => {
    setForm((f) => {
      if (!f) return f;
      const has = f.tiers.includes(tier);
      const tiers = has ? f.tiers.filter((t) => t !== tier) : [...f.tiers, tier];
      return { ...f, tiers };
    });
    setDirty(true);
  }, []);

  const tickCap = config?.tick_cap ?? HTM_SENDS_PER_TICK_MAX;
  const minAgeMs = config?.min_age_ms ?? HTM_MIN_AGE_MS;
  const errors: FieldErrors = form
    ? fieldErrors(form, minAgeMs, tickCap)
    : { age: null, catchup: null, sends: null, tiers: null, urls: null };
  const formInvalid = hasError(errors);

  const save = useCallback(() => {
    if (!form || !config) return;
    const built = buildUpdate(form, minAgeMs, tickCap);
    if ("error" in built) {
      setConfigError(built.error);
      return;
    }
    setSaving(true);
    api
      .putHtmConfig(built.body)
      .then((cfg) => {
        // A Save that passed the floor clears any reverted-age state.
        setConfig((prev) =>
          prev
            ? { ...prev, config: cfg, age_defaulted: false }
            : {
                config: cfg,
                tick_cap: HTM_SENDS_PER_TICK_MAX,
                min_age_ms: HTM_MIN_AGE_MS,
                age_defaulted: false,
              },
        );
        setForm(configToForm(cfg)); // clears the paste field
        setConfigError(null);
        setDirty(false);
        setSavedAt(new Date());
      })
      .catch((e: unknown) => setConfigError(friendlyConfigError(e)))
      .finally(() => setSaving(false));
  }, [form, config, minAgeMs, tickCap]);

  const enabled = config?.config.enabled ?? false;
  const testMode = config?.config.test_mode ?? true;
  const keySet = (config?.config.api_key_secret_arn ?? "") !== "";
  // The badge fails toward TEST: shown when EITHER the loaded config or the
  // latest stats says test_mode is on (a badge wrongly shown is harmless; one
  // wrongly hidden reads test counts as revenue — PRP F6).
  const showTestBadge =
    (config?.config.test_mode ?? false) || (stats?.test_mode_now ?? false);

  // Turning test_mode OFF needs the confirm ONLY while enabled (PRP AC12).
  const onTestModeOff = () => {
    if (enabled) {
      setConfirmingEnable(false);
      setConfirmingTestOff(true);
    } else {
      setFlags({ test_mode: false });
    }
  };

  let healthHint: string;
  if (configLoading && !config) {
    healthHint = "Loading HTM status…";
  } else if (!enabled) {
    healthHint = "HTM is OFF — nothing is pinged or posted.";
  } else if (testMode) {
    healthHint =
      "HTM is ON in TEST MODE — qualifying leads 72 h past their form-fill are pinged and posted with test_mode=1. HTM is not buying these leads.";
  } else {
    healthHint =
      "HTM is ON — LIVE. Qualifying leads 72 h past their form-fill are pinged and, when HTM accepts, posted for real.";
  }

  const selectStyle: CSSProperties = {
    background: tokens.color.bg2,
    color: tokens.color.fg0,
    border: `1px solid ${tokens.color.border}`,
    borderRadius: tokens.radius.md,
    padding: `${tokens.space.sm}px ${tokens.space.md}px`,
    fontSize: tokens.font.size.sm,
    cursor: "pointer",
  };

  const alertStyle: CSSProperties = {
    marginTop: tokens.space.lg,
    padding: `${tokens.space.md}px ${tokens.space.lg}px`,
    borderRadius: tokens.radius.md,
    background: tokens.color.dangerBg,
    border: `1px solid ${tokens.color.dangerBorder}`,
    color: tokens.color.danger,
    fontSize: tokens.font.size.sm,
  };

  const fieldAlertStyle: CSSProperties = {
    marginTop: tokens.space.xs,
    color: tokens.color.danger,
    fontSize: tokens.font.size.xs,
  };

  const presetLabel = PRESETS.find((p) => p.id === preset)?.label ?? "Window";
  const skippedTotal = Object.values(stats?.skips_by_reason ?? {}).reduce(
    (a, n) => a + (Number.isFinite(n) ? n : 0),
    0,
  );

  return (
    <div>
      <PageHeader
        title="HTM"
        subtitle="HTM (Pipes.ai) — resell over-$10K leads 72 h after the form-fill: ping first, post only when HTM accepts"
        actions={
          <div style={{ display: "flex", gap: tokens.space.sm, alignItems: "center", flexWrap: "wrap" }}>
            <select
              value={preset}
              onChange={(e) => setPreset(e.target.value as PresetId)}
              style={selectStyle}
              aria-label="Time range"
            >
              {PRESETS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                color: tokens.color.fg2,
                fontSize: tokens.font.size.sm,
                cursor: "pointer",
              }}
            >
              <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} />
              Auto
            </label>
            <button
              type="button"
              onClick={() => load(true)}
              disabled={liveLoading}
              style={buttonStyle("ghost", liveLoading)}
            >
              {liveLoading ? "…" : "Refresh"}
            </button>
          </div>
        }
      />

      {refreshedAt && (
        <div style={{ color: tokens.color.fg2, fontSize: tokens.font.size.xs, marginBottom: tokens.space.md }}>
          Updated{" "}
          {refreshedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          {auto ? ` · auto-refresh ${POLL_MS / 1000}s` : ""}
          {" · "}
          {presetLabel}
        </div>
      )}

      {/* The two flag toggles — the kill switch (enabled) and test_mode, each
          behind its own confirm gate. */}
      <Card
        style={{
          marginBottom: tokens.space.xl,
          borderColor: enabled ? "rgba(0,212,170,0.28)" : tokens.color.border,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: tokens.space.lg,
            flexWrap: "wrap",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: tokens.space.md }}>
              <span style={{ fontWeight: 700, fontSize: tokens.font.size.lg, color: tokens.color.fg0 }}>
                HTM send
              </span>
              {configLoading && !config ? (
                <Pill tone="neutral">…</Pill>
              ) : enabled ? (
                <Pill tone="success">On</Pill>
              ) : (
                <Pill tone="neutral">Off</Pill>
              )}
            </div>
            <p style={{ margin: `${tokens.space.sm}px 0 0`, color: tokens.color.fg1, fontSize: tokens.font.size.sm }}>
              {healthHint}
            </p>
            {config && !keySet && (
              <p style={{ margin: `${tokens.space.xs}px 0 0`, color: tokens.color.warning, fontSize: tokens.font.size.sm }}>
                No API key is set — every tick skips (no_api_key) until one is pasted below.
              </p>
            )}
          </div>

          <div style={{ display: "flex", gap: tokens.space.sm, alignItems: "center" }}>
            {toggling ? (
              <button type="button" disabled style={buttonStyle("ghost", true)}>
                Working…
              </button>
            ) : enabled ? (
              <button
                type="button"
                onClick={() => setFlags({ enabled: false })}
                aria-label="Turn HTM off"
                style={buttonStyle("danger")}
              >
                Turn Off
              </button>
            ) : confirmingEnable ? (
              <>
                <button
                  type="button"
                  onClick={() => setFlags({ enabled: true })}
                  aria-label="Confirm — start sending leads to HTM"
                  style={buttonStyle("primary")}
                >
                  {testMode ? "Yes, start sending (TEST)" : "Yes, start sending LIVE"}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingEnable(false)}
                  aria-label="Cancel enable"
                  style={buttonStyle("ghost")}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setConfirmingTestOff(false);
                  setConfirmingEnable(true);
                }}
                aria-label="Turn HTM on"
                disabled={configLoading || !config}
                style={buttonStyle("primary", configLoading || !config)}
              >
                Turn On
              </button>
            )}
          </div>
        </div>

        {confirmingEnable && !enabled && (
          <div role="alert" style={alertStyle}>
            {testMode ? (
              <>
                <strong>Are you sure — this starts sending leads to HTM in TEST MODE.</strong>{" "}
                The next tick pings HTM for qualifying over-$10K leads 72 h past their form-fill and
                posts the accepted ones with test_mode=1 — real consumer data, not a sale. Off is
                immediate; on is deliberate.
              </>
            ) : (
              <>
                <strong>Are you sure — this starts sending LIVE leads to HTM.</strong> Test mode is
                OFF: the next tick pings HTM for qualifying over-$10K leads 72 h past their form-fill
                and posts the accepted ones for real. Off is immediate; on is deliberate.
              </>
            )}
          </div>
        )}

        {/* test_mode — ON is immediate (the safe direction); OFF needs the
            confirm while the stage is enabled. */}
        <div
          style={{
            marginTop: tokens.space.lg,
            paddingTop: tokens.space.lg,
            borderTop: `1px solid ${tokens.color.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: tokens.space.lg,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: tokens.space.md }}>
            <span style={{ fontWeight: 600, color: tokens.color.fg0 }}>Test mode</span>
            {configLoading && !config ? (
              <Pill tone="neutral">…</Pill>
            ) : testMode ? (
              <Pill tone="warning">On</Pill>
            ) : (
              <Pill tone="success">Off — live</Pill>
            )}
          </div>
          <div style={{ display: "flex", gap: tokens.space.sm, alignItems: "center" }}>
            {toggling ? null : testMode ? (
              confirmingTestOff && enabled ? (
                <>
                  <button
                    type="button"
                    onClick={() => setFlags({ test_mode: false })}
                    aria-label="Confirm — turn test mode off and send LIVE leads to HTM"
                    style={buttonStyle("danger")}
                  >
                    Yes, go live
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmingTestOff(false)}
                    aria-label="Cancel test mode off"
                    style={buttonStyle("ghost")}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={onTestModeOff}
                  aria-label="Turn test mode off"
                  disabled={configLoading || !config}
                  style={buttonStyle("ghost", configLoading || !config)}
                >
                  Turn test mode off
                </button>
              )
            ) : (
              <button
                type="button"
                onClick={() => setFlags({ test_mode: true })}
                aria-label="Turn test mode on"
                disabled={configLoading || !config}
                style={buttonStyle("ghost", configLoading || !config)}
              >
                Turn test mode on
              </button>
            )}
          </div>
        </div>

        {confirmingTestOff && enabled && testMode && (
          <div role="alert" style={alertStyle}>
            <strong>Are you sure — this makes HTM sends LIVE.</strong> HTM is enabled, so the next
            tick posts accepted leads WITHOUT test_mode — real sales. Only do this once HTM has
            confirmed the test leads landed.
          </div>
        )}

        {toggleError && (
          <div role="alert" style={{ marginTop: tokens.space.md, color: tokens.color.danger, fontSize: tokens.font.size.sm }}>
            {toggleError}
          </div>
        )}
      </Card>

      {config?.age_defaulted && (
        <Card style={{ marginBottom: tokens.space.xl }}>
          <div role="alert" style={{ color: tokens.color.danger, fontSize: tokens.font.size.sm }}>
            <strong>HTM is sending nothing (invalid_age).</strong> The stored age was below the
            72-hour floor, so the dispatcher skips every tick until a valid age is saved below.
          </div>
        </Card>
      )}

      {liveError && !stats && (
        <Card style={{ marginBottom: tokens.space.xl }}>
          <EmptyState title="Failed to load stats" hint={liveError} />
        </Card>
      )}

      {/* Stats strip — the funnel (Logs Insights) + ledger outcomes split
          LIVE vs TEST + HTM's rejection reasons. */}
      <section aria-label="HTM stats" style={{ marginBottom: tokens.space.xl }}>
        {showTestBadge && (
          <div
            role="status"
            aria-label="TEST MODE"
            style={{
              display: "flex",
              alignItems: "center",
              gap: tokens.space.md,
              marginBottom: tokens.space.lg,
              padding: `${tokens.space.md}px ${tokens.space.lg}px`,
              borderRadius: tokens.radius.md,
              background: "rgba(212,168,83,0.14)",
              border: `2px solid ${tokens.color.warning}`,
              color: tokens.color.warning,
            }}
          >
            <span style={{ fontWeight: 800, fontSize: tokens.font.size.lg, letterSpacing: "0.08em" }}>
              TEST MODE
            </span>
            <span style={{ fontSize: tokens.font.size.sm }}>
              sends carry test_mode=1 — HTM is not buying these leads; test counts are not revenue
            </span>
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: tokens.space.lg,
            marginBottom: tokens.space.lg,
          }}
        >
          {liveLoading && !stats ? (
            <>
              <SkeletonStat />
              <SkeletonStat />
              <SkeletonStat />
              <SkeletonStat />
              <SkeletonStat />
            </>
          ) : (
            <>
              <StatCard label="Considered" value={fmtCount(stats?.considered)} hint="unique leads the tick evaluated" emphasis />
              <StatCard label="Skipped" value={fmtCount(skippedTotal)} hint="unique leads, by reason below" />
              <StatCard label="Pinged" value={fmtCount(stats?.pinged)} hint="unique leads pinged to HTM" />
              <StatCard label="Deadline-hit ticks" value={fmtCount(stats?.deadline_hit_ticks)} hint="ticks that stopped at the 30 s guard" />
              <StatCard label="Band-saturated ticks" value={fmtCount(stats?.band_saturated_ticks)} hint="ticks whose band was fuller than the candidate limit" />
            </>
          )}
        </div>

        <OutcomeGroup title="Live outcomes" heading="Live" counts={stats?.outcomes.live} loading={liveLoading && !stats} />
        <OutcomeGroup
          title="Test outcomes"
          heading="Test — sent with test_mode=1, not revenue"
          counts={stats?.outcomes.test}
          loading={liveLoading && !stats}
        />

        {stats && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: tokens.space.lg }}>
            <ReasonList title="Ping rejected — HTM reason" counts={stats.rejected_by_reason.ping} />
            <ReasonList title="Post rejected — HTM reason" counts={stats.rejected_by_reason.post} />
            <ReasonList title="Skipped — reason" counts={stats.skips_by_reason} />
          </div>
        )}
      </section>

      {/* Config form */}
      <Card title="Configuration" style={{ marginBottom: tokens.space.xl }}>
        {configLoading && !form ? (
          <Skeleton height={280} />
        ) : configError && !form ? (
          <EmptyState title="Failed to load config" hint={configError} />
        ) : form ? (
          <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.lg }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: tokens.space.lg }}>
              <div>
                <FieldLabel htmlFor="htm-age-hours" hint={`hours after the form-fill before a lead is sent — never less than ${minAgeMs / HOUR_MS}`}>
                  Age (hours)
                </FieldLabel>
                <input
                  id="htm-age-hours"
                  type="number"
                  min={minAgeMs / HOUR_MS}
                  step="1"
                  value={form.ageHours}
                  onChange={(e) => update("ageHours", e.target.value)}
                  style={{
                    ...inputStyle,
                    borderColor: errors.age ? tokens.color.dangerBorder : tokens.color.borderMid,
                  }}
                  aria-label="Age (hours)"
                />
                {errors.age && (
                  <div role="alert" style={fieldAlertStyle}>
                    {errors.age}
                  </div>
                )}
              </div>
              <div>
                <FieldLabel htmlFor="htm-catchup-min" hint="width of the form-fill band each tick walks — 5 to 360 minutes">
                  Catch-up (minutes)
                </FieldLabel>
                <input
                  id="htm-catchup-min"
                  type="number"
                  min={HTM_CATCHUP_MIN_MS / MIN_MS}
                  max={HTM_CATCHUP_MAX_MS / MIN_MS}
                  step="1"
                  value={form.catchupMin}
                  onChange={(e) => update("catchupMin", e.target.value)}
                  style={{
                    ...inputStyle,
                    borderColor: errors.catchup ? tokens.color.dangerBorder : tokens.color.borderMid,
                  }}
                  aria-label="Catch-up (minutes)"
                />
                {errors.catchup && (
                  <div role="alert" style={fieldAlertStyle}>
                    {errors.catchup}
                  </div>
                )}
              </div>
              <div>
                <FieldLabel htmlFor="htm-sends-per-tick" hint={`pings per 5-minute tick, capped at ${tickCap}`}>
                  Sends per tick
                </FieldLabel>
                <input
                  id="htm-sends-per-tick"
                  type="number"
                  min="1"
                  max={tickCap}
                  step="1"
                  value={form.sendsPerTick}
                  onChange={(e) => update("sendsPerTick", e.target.value)}
                  style={{
                    ...inputStyle,
                    borderColor: errors.sends ? tokens.color.dangerBorder : tokens.color.borderMid,
                  }}
                  aria-label="Sends per tick"
                />
                {errors.sends && (
                  <div role="alert" style={fieldAlertStyle}>
                    {errors.sends}
                  </div>
                )}
              </div>
            </div>

            <div>
              <FieldLabel hint="HTM only sees leads whose route tier is checked (at least one; under-$10K is never sent)">
                Tiers HTM covers
              </FieldLabel>
              <div
                role="group"
                aria-label="Tiers HTM covers"
                style={{ display: "flex", flexWrap: "wrap", gap: tokens.space.md }}
              >
                {HTM_TIERS.map((t) => (
                  <label
                    key={t}
                    style={{ display: "flex", alignItems: "center", gap: 6, color: tokens.color.fg0, fontSize: tokens.font.size.sm, cursor: "pointer" }}
                  >
                    <input
                      type="checkbox"
                      checked={form.tiers.includes(t)}
                      onChange={() => toggleTier(t)}
                      aria-label={TIER_LABELS[t]}
                    />
                    {TIER_LABELS[t]}
                  </label>
                ))}
              </div>
              {errors.tiers && (
                <div role="alert" style={fieldAlertStyle}>
                  {errors.tiers}
                </div>
              )}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: tokens.space.lg }}>
              <div>
                <FieldLabel htmlFor="htm-ping-fields" hint="hashed = md5 phone + email on the ping (default); raw = the plain values">
                  Ping identity
                </FieldLabel>
                <select
                  id="htm-ping-fields"
                  value={form.pingFields}
                  onChange={(e) => update("pingFields", e.target.value as HtmPingFields)}
                  style={{ ...selectStyle, width: "100%" }}
                  aria-label="Ping identity"
                >
                  <option value="hashed">Hashed (md5)</option>
                  <option value="raw">Raw</option>
                </select>
              </div>
              <div>
                <FieldLabel htmlFor="htm-ping-url" hint="must start with https://">
                  Ping URL
                </FieldLabel>
                <input
                  id="htm-ping-url"
                  type="text"
                  value={form.pingUrl}
                  onChange={(e) => update("pingUrl", e.target.value)}
                  style={inputStyle}
                  aria-label="Ping URL"
                />
              </div>
              <div>
                <FieldLabel htmlFor="htm-post-url" hint="must start with https://">
                  Post URL
                </FieldLabel>
                <input
                  id="htm-post-url"
                  type="text"
                  value={form.postUrl}
                  onChange={(e) => update("postUrl", e.target.value)}
                  style={inputStyle}
                  aria-label="Post URL"
                />
              </div>
            </div>
            {errors.urls && (
              <div role="alert" style={fieldAlertStyle}>
                {errors.urls}
              </div>
            )}

            <div>
              <FieldLabel
                htmlFor="htm-api-key"
                hint="stored securely in Secrets Manager; only a masked hint is ever shown. Paste to set / replace — leave blank to keep the current key"
              >
                HTM API key
              </FieldLabel>
              <div style={{ marginBottom: tokens.space.xs, color: tokens.color.fg1, fontSize: tokens.font.size.sm }}>
                Current key:{" "}
                <span style={{ fontFamily: tokens.font.mono }}>{config?.config.api_key_hint ?? "not set"}</span>
              </div>
              <input
                id="htm-api-key"
                type="password"
                autoComplete="off"
                spellCheck={false}
                placeholder="paste to set / replace"
                value={form.apiKeyNew}
                onChange={(e) => update("apiKeyNew", e.target.value)}
                style={{ ...inputStyle, maxWidth: 420 }}
                aria-label="HTM API key"
              />
            </div>

            {configError && (
              <div role="alert" style={{ color: tokens.color.danger, fontSize: tokens.font.size.sm }}>
                {configError}
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: tokens.space.md }}>
              <button
                type="button"
                onClick={save}
                disabled={!dirty || saving || formInvalid}
                style={buttonStyle("primary", !dirty || saving || formInvalid)}
              >
                {saving ? "Saving…" : "Save"}
              </button>
              {savedAt && !dirty && (
                <span style={{ color: tokens.color.fg2, fontSize: tokens.font.size.xs }}>
                  Saved {savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                </span>
              )}
            </div>
          </div>
        ) : (
          <EmptyState title="No config loaded" />
        )}
      </Card>
    </div>
  );
}

/** One ledger-outcome tile group (LIVE or TEST). A named region, so the two
 *  groups are distinguishable to a screen reader and to the tests. */
function OutcomeGroup({
  title,
  heading,
  counts,
  loading,
}: {
  title: string;
  heading: string;
  counts: HtmOutcomeCounts | undefined;
  loading: boolean;
}) {
  return (
    <section aria-label={title} style={{ marginBottom: tokens.space.lg }}>
      <div
        style={{
          color: tokens.color.fg1,
          fontSize: tokens.font.size.sm,
          fontWeight: 600,
          marginBottom: tokens.space.sm,
        }}
      >
        {heading}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: tokens.space.lg }}>
        {loading
          ? OUTCOME_TILES.map((t) => <SkeletonStat key={t.status} />)
          : OUTCOME_TILES.map((t) => (
              <StatCard key={t.status} label={t.label} value={fmtCount(counts?.[t.status])} hint={t.hint} />
            ))}
      </div>
    </section>
  );
}

/** A reason → count list (HTM's rejection reasons, or the skip reasons). */
function ReasonList({ title, counts }: { title: string; counts: Record<string, number> }) {
  const rows = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  return (
    <Card title={title}>
      {rows.length === 0 ? (
        <div style={{ color: tokens.color.fg2, fontSize: tokens.font.size.sm }}>None in this window.</div>
      ) : (
        <ul aria-label={title} style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {rows.map(([reason, n]) => (
            <li
              key={reason}
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: tokens.space.md,
                padding: `${tokens.space.xs}px 0`,
                fontSize: tokens.font.size.sm,
                color: tokens.color.fg0,
              }}
            >
              <span style={{ wordBreak: "break-word" }}>{reason}</span>
              <span style={{ fontFamily: tokens.font.mono }}>{fmtCount(n)}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function SkeletonStat() {
  return (
    <div
      style={{
        background: tokens.color.bg2,
        border: `1px solid ${tokens.color.border}`,
        borderRadius: tokens.radius.lg,
        padding: tokens.space.lg,
      }}
    >
      <Skeleton height={10} width="40%" />
      <div style={{ height: tokens.space.sm }} />
      <Skeleton height={28} width="70%" />
      <div style={{ height: tokens.space.xs }} />
      <Skeleton height={10} width="50%" />
    </div>
  );
}
